<template>
    <Modal close-esc-event="keydown" :close-button="false" modal-class=" m-auto border-0" mask-class="grid" @opening="onOpening" @closing="onClose">
        <template #toggle="scope">
            <CustomButton class="px-6 !w-auto" @click="scope.open">
                {{ $t('views.reports.reportsPage.eControlModal.modalButtonLabel') }}
            </CustomButton>
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body>
            <div class="bg-gray-100 py-6 px-8 rounded-xl">
                <transition name="fade" mode="out-in">
                    <div v-if="pdf" class="space-y-6">
                        <h2 class="text-xl font-semibold text-center">{{ header }}</h2>
                        <div class="flex space-x-4">
                            <button
                                v-for="eControlAction of eControlActions"
                                :key="eControlAction.text"
                                type="button"
                                class="bg-primary-700 text-primary-800 bg-opacity-20 hover:bg-opacity-30 transition-colors rounded-lg py-4 px-3 flex w-40 justify-center space-x-2"
                                @click="eControlAction.onClick"
                            >
                                <span class="my-auto justify-self-end">
                                    <Icon :src="eControlAction.icon"></Icon>
                                </span>
                                <span class="my-auto font-medium justify-self-start">{{ eControlAction.text }}</span>
                            </button>
                        </div>
                    </div>
                    <div v-else class="w-80 space-y-6 flex-col">
                        <h2 class="text-xl font-semibold text-center">{{ header }}</h2>
                        <div class="flex justify-center mx-auto">
                            <Spinner class="w-16 h-16" :active="true"></Spinner>
                        </div>
                    </div>
                </transition>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import { useToast } from 'vue-toastification';
import CustomButton from '@/components/ui/CustomButton.vue';
import Spinner from '@/components/ui/Spinner.vue';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import Icon from '@/components/icons/Icon.vue';
import { ActionType } from '@/plugins/store/actions';
import { store } from '@/plugins/store';
import { downloadBlob } from '@/util/downloadFile';
import { ReportContentOption, ReportViewState, IconSource } from '@/types';

export default defineComponent({
    components: { Modal, CustomButton, Spinner, CloseModalButton, Icon },
    props: {
        state: {
            type: Object as () => ReportViewState,
            required: true,
        },
        commentDifference: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            pdf: null as null | Blob,
            toast: useToast(),
        };
    },
    computed: {
        header(): string {
            return this.pdf ? this.$t('views.reports.reportsPage.eControlModal.generatedModalHeader') : this.$t('views.reports.reportsPage.eControlModal.generatingModalHeader');
        },
        eControlActions(): { icon: string | number; text: string; onClick: CallableFunction }[] {
            return [
                {
                    icon: IconSource.DownloadPDF,
                    text: this.$t('views.reports.reportsPage.eControlModal.savePdfButtonLabel'),
                    onClick: () => {
                        this.downloadPdf();
                    },
                },
                {
                    icon: IconSource.ShowPDF,
                    text: this.$t('views.reports.reportsPage.eControlModal.showButtonLabel'),
                    onClick: () => {
                        this.openPdf();
                    },
                },
            ];
        },
    },
    methods: {
        async onOpening() {
            try {
                await this.ensureContentOptionsSelected();
                const reconciliationIds = this.$props.state.reconciliationIds ?? [];
                const response = await store.dispatch(ActionType.GetEControlPDF, {
                    form: {
                        site_ids: this.$props.state.siteIds,
                        dates: this.$props.state.dates,
                        content_option_ids: this.$props.state.contentOptionIds,
                        comment_difference: this.commentDifference || undefined,
                        reconciliation_ids: reconciliationIds.length ? reconciliationIds : undefined,
                        report_type: this.$props.state.isBatchReport ? 'batch' : 'farm',
                    },
                });
                this.pdf = new Blob([response], { type: 'application/pdf' });
            } catch (error: any) {
                console.log(error);
                this.toast?.error(this.$t('global.errors.unknown'));
            }
        },
        onClose() {
            this.pdf = null;
        },
        async ensureContentOptionsSelected() {
            if (this.$props.state.contentOptionIds.length || this.$props.state.contentOptionsTouched) {
                return;
            }

            const options: ReportContentOption[] = this.$props.state.contentOptions.length
                ? this.$props.state.contentOptions
                : await store.dispatch(ActionType.GetReportContentOptions);
            if (!this.$props.state.contentOptions.length) {
                this.$props.state.contentOptions = options;
            }
            const activeIds = options.filter((option) => option.is_active).map((option) => option.id);
            this.$props.state.contentOptionIds.splice(0, this.$props.state.contentOptionIds.length, ...activeIds);
        },
        downloadPdf() {
            if (!this.pdf) return;
            downloadBlob(this.pdf, `${new Date().toLocaleDateString().replaceAll('.', '-')}_e-control.pdf`);
        },
        openPdf() {
            if (!this.pdf) return;
            const fileURL = URL.createObjectURL(this.pdf);
            window.open(fileURL);

            // Not revoked inline: window.open loads the new document asynchronously, so dropping the
            // URL in this tick would race the load. Same delay as useGetAndOpenRouteItem.ts.
            setTimeout(() => URL.revokeObjectURL(fileURL), 200);
        },
    },
});
</script>
